import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, RefreshCcw, BarChart3, Phone, CheckCircle } from 'lucide-react';

export default function Home() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const services = [
    {
      id: 'lead-generation',
      icon: Target,
      title: 'Lead Generation',
      subtitle: 'Prospection téléphonique',
      description: 'Acquisition de prospects qualifiés grâce au cold call.',
    },
    {
      id: 'sales-360',
      icon: RefreshCcw,
      title: 'Sales 360',
      subtitle: 'Cycle de vente complet',
      description: 'Externalisation complète de votre force de vente pour maximiser vos résultats.',
    },
    {
      id: 'consulting',
      icon: BarChart3,
      title: 'Consulting',
      subtitle: 'Stratégie & Formation',
      description: 'Audit et optimisation de vos processus commerciaux avec accompagnement personnalisé.',
    },
  ];

  const benefits = [
    'Réduction des coûts de recrutement',
    'Équipe commerciale expérimentée',
    'Résultats mesurables et transparents',
    'Flexibilité et adaptation rapide',
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#ECFDF5] to-white" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              {/* Tag */}
              <div className="inline-flex items-center gap-2 mb-6 bg-[#E8F5F3] rounded-full px-4 py-2">
                <span className="text-[#10B981] text-sm font-medium">Expert commercial B2B</span>
              </div>

              {/* Main Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight mb-6" data-testid="hero-title">
                Boostez vos <span className="text-[#10B981]">ventes B2B</span> avec 0to1
              </h1>

              {/* Subheadline */}
              <p className="text-lg text-[#1E3A5F] font-semibold mb-8 leading-relaxed" data-testid="hero-subtitle">
                Votre partenaire commercial B2B. Plus de 7 années d'expérience en vente complexe pour transformer vos opportunités en succès.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 bg-[#10B981] text-white font-semibold rounded-full px-8 py-4 hover:bg-[#0D6B5E] transition-all duration-200 group"
                >
                  Démarrer maintenant
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#10B981] text-[#10B981] font-semibold rounded-full px-8 py-4 hover:bg-[#E8F5F3] transition-all duration-200"
                >
                  Découvrir les services
                </Link>
              </div>
            </div>

            {/* Hero Image/Visual */}
            <div className="hidden lg:block relative">
              <div className="bg-[#10B981] rounded-3xl p-8 transform rotate-3 shadow-2xl">
                <div className="bg-white rounded-2xl p-6 transform -rotate-3">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E8F5F3] rounded-full flex items-center justify-center">
                        <Target size={20} className="text-[#10B981]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1A1A1A]">Leads contactés</p>
                        <p className="text-xs text-[#1E3A5F] font-semibold">+127 ce jour</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E8F5F3] rounded-full flex items-center justify-center">
                        <RefreshCcw size={20} className="text-[#10B981]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1A1A1A]">Rendez-vous qualifiés</p>
                        <p className="text-xs text-[#1E3A5F] font-semibold">3  en moyenne</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#E8F5F3] rounded-full flex items-center justify-center">
                        <BarChart3 size={20} className="text-[#10B981]" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#1A1A1A]">ROI client</p>
                        <p className="text-xs text-[#1E3A5F] font-semibold">x3 en 6 mois</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Services proposés</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">
              Des solutions sur mesure pour votre croissance
            </h2>
            <p className="text-[#1E3A5F] font-semibold text-lg max-w-2xl mx-auto">
              Une gamme complète d'options pour accélérer votre développement commercial.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={`/services#${service.id}`}
                data-testid={`service-card-${index}`}
                className="bg-[#ECFDF5] border border-gray-100 p-8 rounded-2xl hover:border-[#10B981] hover:shadow-lg transition-all duration-300 group"
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-[#E8F5F3] flex items-center justify-center mb-6 group-hover:bg-[#10B981] transition-colors duration-300">
                  <service.icon size={28} className="text-[#10B981] group-hover:text-white transition-colors duration-300" />
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">
                  {service.title}
                </h3>
                <p className="text-[#10B981] text-sm font-medium mb-4">{service.subtitle}</p>
                <p className="text-[#1E3A5F] font-semibold text-sm leading-relaxed">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-[#ECFDF5]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">
                Une approche moderne de la prospection commerciale
              </h2>
              <p className="text-[#1E3A5F] font-semibold text-lg mb-8">
                Confiez votre développement commercial à un expert et concentrez-vous sur votre cœur de métier.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle size={22} className="text-[#10B981] flex-shrink-0" />
                    <span className="text-[#1A1A1A] font-medium">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
              <div className="text-center">
                <div className="text-6xl font-bold text-[#10B981] mb-2">100%</div>
                <p className="text-[#1E3A5F] font-semibold mb-6">Taux de satisfaction accompagnement</p>
                <div className="border-t border-gray-100 pt-6">
                  <p className="text-[#1A1A1A] font-medium italic">
                    "Jehan a transformé mon approche commerciale. Des résultats concrets dès le premier mois."
                  </p>
                  <p className="text-[#10B981] text-sm mt-4 font-medium">— Partenaire Cybersécurité</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#10B981]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à transformer vos prospects en clients ?
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-xl mx-auto">
            Échangeons sur vos objectifs commerciaux et définissons ensemble la meilleure stratégie pour booster vos ventes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0658930595"
              data-testid="cta-contact-btn"
              className="inline-flex items-center justify-center gap-3 bg-white text-[#10B981] font-semibold rounded-full px-8 py-4 hover:bg-gray-100 transition-all duration-200"
            >
              <Phone size={20} />
              06 58 93 05 95
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white text-white font-semibold rounded-full px-8 py-4 hover:bg-white/10 transition-all duration-200"
            >
              Envoyer un message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
